import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTrash,
  faQrcode,
  faPrint,
  faMapMarkerAlt,
  faSearch,
  faCrosshairs,
} from '@fortawesome/free-solid-svg-icons';
import { QRCodeSVG } from 'qrcode.react';
import CommonTable from '../../table/ComonTable';
import CommonModal from '../../commonmodal/CommonModal';
import DeleteModal from '../../commonmodal/DeleteModal';
import { roundInspectionApi } from '../roundInspectionApi';

const CheckpointMasterList = ({ siteId }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Add / Edit Modal State
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  // Print Sheet Modal State
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [selectedForPrint, setSelectedForPrint] = useState([]);

  // QR Preview Modal State
  const [previewQrItem, setPreviewQrItem] = useState(null);

  // Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form State
  const initialForm = {
    locationCode: '',
    locationName: '',
    wingId: 0,
    floorId: 0,
    latitude: '',
    longitude: '',
    geofenceRadiusMeters: 25,
    notes: '',
    isActiveId: 1,
  };
  const [formData, setFormData] = useState(initialForm);

  const fetchLocations = useCallback(async () => {
    const activeSiteId = siteId || 2;
    setLoading(true);
    try {
      const res = await roundInspectionApi.getLocations(activeSiteId);
      const data = res?.data?.data || res?.data || [];
      if (Array.isArray(data)) {
        setLocations(data);
      } else {
        setLocations([]);
      }
    } catch (err) {
      toast.error('Failed to load inspection checkpoints: ' + (err.message || ''));
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  // Capture GPS from browser
  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.warn('Geolocation is not supported by your browser');
      return;
    }
    toast.info('Fetching current device coordinates...');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setFormData((prev) => ({
          ...prev,
          latitude: pos.coords.latitude.toFixed(6),
          longitude: pos.coords.longitude.toFixed(6),
        }));
        toast.success('Coordinates captured successfully!');
      },
      (err) => {
        toast.error('Could not get coordinates: ' + err.message);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Open Add modal
  const handleOpenAdd = () => {
    setIsEditMode(false);
    setEditId(null);
    setFormData({
      ...initialForm,
      locationCode: 'CP-' + Math.floor(1000 + Math.random() * 9000),
    });
    setIsFormOpen(true);
  };

  // Open Edit modal
  const handleOpenEdit = (loc) => {
    const locId = loc.locationId || loc.id;
    setIsEditMode(true);
    setEditId(locId);
    setFormData({
      locationCode: loc.locationCode || '',
      locationName: loc.locationName || '',
      wingId: loc.wingId || 0,
      floorId: loc.floorId || 0,
      latitude: loc.latitude !== null && loc.latitude !== undefined ? loc.latitude : '',
      longitude: loc.longitude !== null && loc.longitude !== undefined ? loc.longitude : '',
      geofenceRadiusMeters: loc.geofenceRadiusMeters || loc.allowedRadiusMeters || 25,
      notes: loc.notes || '',
      isActiveId: loc.isActiveId !== undefined ? loc.isActiveId : (loc.isActive ? 1 : 0),
    });
    setIsFormOpen(true);
  };

  // Save Checkpoint
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!formData.locationCode.trim() || !formData.locationName.trim()) {
      toast.warn('Please fill in both Location Code and Location Name');
      return;
    }

    const locId = isEditMode ? editId : 0;
    const radius = parseInt(formData.geofenceRadiusMeters, 10) || 25;
    const activeVal = parseInt(formData.isActiveId, 10);

    const payload = {
      id: locId,
      locationId: locId,
      siteId: Number(siteId) || 2,
      wingId: Number(formData.wingId) || 0,
      floorId: Number(formData.floorId) || 0,
      locationCode: formData.locationCode.trim().toUpperCase(),
      locationName: formData.locationName.trim(),
      latitude: formData.latitude !== '' ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude !== '' ? parseFloat(formData.longitude) : null,
      allowedRadiusMeters: radius,
      geofenceRadiusMeters: radius,
      notes: formData.notes?.trim() || '',
      isActive: activeVal === 1,
      isActiveId: activeVal,
    };

    try {
      if (isEditMode) {
        await roundInspectionApi.updateLocation(editId, payload);
        toast.success('Checkpoint updated successfully');
      } else {
        await roundInspectionApi.createLocation(payload);
        toast.success('Checkpoint registered successfully');
      }
      setIsFormOpen(false);
      fetchLocations();
    } catch (err) {
      const errDetail = err.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join(', ')
        : (err.response?.data?.message || err.message);
      toast.error('Failed to save checkpoint: ' + errDetail);
    }
  };

  // Confirm Delete
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      const locId = itemToDelete.locationId || itemToDelete.id;
      await roundInspectionApi.deleteLocation(locId);
      toast.success('Checkpoint deactivated');
      setDeleteModalOpen(false);
      fetchLocations();
    } catch (err) {
      toast.error('Failed to delete: ' + (err.message || ''));
    } finally {
      setIsDeleting(false);
    }
  };

  // Filtered List
  const filteredLocations = locations.filter((loc) => {
    const term = searchTerm.toLowerCase();
    return (
      (loc.locationCode || '').toLowerCase().includes(term) ||
      (loc.locationName || '').toLowerCase().includes(term)
    );
  });

  // Print Sheet
  const handleOpenPrintSheet = () => {
    setSelectedForPrint(filteredLocations.map((l) => l.locationId || l.id));
    setIsPrintModalOpen(true);
  };

  const togglePrintSelect = (id) => {
    setSelectedForPrint((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleTriggerPrint = () => {
    window.print();
  };

  // Table Configuration matching project standard
  const tableHeaders = [
    { label: 'S. NO', key: 'sno', align: 'center' },
    { label: 'LOCATION CODE', key: 'locationCode', align: 'left' },
    { label: 'CHECKPOINT NAME', key: 'locationName', align: 'left' },
    { label: 'GPS GEOFENCE', key: 'geofence', align: 'center' },
    { label: 'QR TAG', key: 'qrTag', align: 'center' },
    { label: 'STATUS', key: 'status', align: 'center' },
    { label: 'ACTIONS', key: 'actions', align: 'center' },
  ];

  const renderCell = (key, row, header) => {
    switch (key) {
      case 'sno':
        return <span className="font-semibold text-xs text-textsecondary">{filteredLocations.indexOf(row) + 1}</span>;
      case 'locationCode':
        return (
          <span className="px-2.5 py-1 rounded text-xs font-mono font-bold bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200">
            {row.locationCode}
          </span>
        );
      case 'locationName':
        return (
          <div>
            <div className="font-bold text-xs text-textprimary">{row.locationName}</div>
            <div className="text-[11px] text-textsecondary">
              {row.wingName || 'Wing: Main'} &bull; {row.floorName || 'Ground'}
            </div>
          </div>
        );
      case 'geofence': {
        const radius = row.geofenceRadiusMeters || row.allowedRadiusMeters || 25;
        return row.latitude && row.longitude ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300 border border-blue-200 dark:border-blue-900">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="text-red-500" />
            {parseFloat(row.latitude).toFixed(4)}, {parseFloat(row.longitude).toFixed(4)} ({radius}m)
          </span>
        ) : (
          <span className="text-[11px] text-textsecondary italic">No GPS bounds</span>
        );
      }
      case 'qrTag':
        return (
          <button
            onClick={() => setPreviewQrItem(row)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors"
          >
            <FontAwesomeIcon icon={faQrcode} />
            Preview
          </button>
        );
      case 'status': {
        const isActive = row.isActiveId === 1 || row.isActive === true;
        return isActive ? (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-50 text-green-700 border border-green-200">
            Active
          </span>
        ) : (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-500 border border-gray-200">
            Inactive
          </span>
        );
      }
      case 'actions':
        return (
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => handleOpenEdit(row)}
              className="text-blue-600 hover:text-blue-800 transition-colors text-xs"
              title="Edit Checkpoint"
            >
              <FontAwesomeIcon icon={faEdit} />
            </button>
            <button
              onClick={() => {
                setItemToDelete(row);
                setDeleteModalOpen(true);
              }}
              className="text-red-500 hover:text-red-700 transition-colors text-xs"
              title="Deactivate Checkpoint"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        );
      default:
        return row[key] || '-';
    }
  };

  const headerActions = (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Search Input */}
      <div className="relative flex items-center">
        <FontAwesomeIcon icon={faSearch} className="absolute left-3 text-gray-400 text-xs" />
        <input
          type="text"
          placeholder="Search by code or name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-bordercolor bg-white dark:bg-cardbg text-textprimary focus:outline-none focus:ring-1 focus:ring-indigo-500 w-48 sm:w-60"
        />
      </div>

      {/* Print Sticker Sheet Button */}
      <button
        onClick={handleOpenPrintSheet}
        disabled={filteredLocations.length === 0}
        className="border border-bordercolor px-3 py-1.5 bg-cardbg hover:bg-gray-50 text-textprimary text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors disabled:opacity-50"
      >
        <FontAwesomeIcon icon={faPrint} />
        Print Sticker Sheet ({filteredLocations.length})
      </button>

      {/* Add Checkpoint Button */}
      <button
        onClick={handleOpenAdd}
        className="px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
      >
        <FontAwesomeIcon icon={faPlus} />
        Add Checkpoint
      </button>
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Table Component */}
      <div className="border border-gray-200 dark:border-bordercolor rounded-xl overflow-hidden shadow-sm">
        <CommonTable
          loading={loading}
          tableHeading="Checkpoints Master & QR Tags"
          headers={tableHeaders}
          data={filteredLocations}
          renderCell={renderCell}
          headerAction={headerActions}
          onEdit={handleOpenEdit}
          onDelete={(row) => {
            setItemToDelete(row);
            setDeleteModalOpen(true);
          }}
          customActions={[
            {
              label: 'Preview QR Tag',
              icon: faQrcode,
              onClick: (row) => setPreviewQrItem(row),
            },
          ]}
        />
      </div>

      {/* Add / Edit Checkpoint Modal using CommonModal */}
      <CommonModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        title={isEditMode ? 'Edit Patrol Checkpoint' : 'Register Inspection Checkpoint'}
        subTitle="Set up physical patrol location with code and optional anti-spoofing GPS bounds."
        footerContent={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setIsFormOpen(false)}
              className="px-4 py-2 text-xs font-medium rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmitForm}
              className="px-4 py-2 text-xs font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 transition-colors shadow-sm"
            >
              {isEditMode ? 'Save Changes' : 'Create Checkpoint'}
            </button>
          </div>
        }
      >
        <form onSubmit={handleSubmitForm} className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-textprimary mb-1 block">
              Location Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.locationCode}
              onChange={(e) => setFormData({ ...formData, locationCode: e.target.value })}
              placeholder="e.g. CP-B1-PUMP"
              className="w-full border border-gray-300 dark:border-bordercolor rounded-lg p-2.5 text-xs font-mono bg-white dark:bg-cardbg text-textprimary focus:outline-none focus:ring-1 focus:ring-indigo-500"
              required
            />
            <span className="text-[11px] text-textsecondary mt-0.5 block">
              Unique identifier printed directly onto the physical QR sticker.
            </span>
          </div>

          <div>
            <label className="text-xs font-semibold text-textprimary mb-1 block">
              Checkpoint Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.locationName}
              onChange={(e) => setFormData({ ...formData, locationName: e.target.value })}
              placeholder="e.g. Basement 1 - Water Pump Room"
              className="w-full border border-gray-300 dark:border-bordercolor rounded-lg p-2.5 text-xs bg-white dark:bg-cardbg text-textprimary focus:outline-none focus:ring-1 focus:ring-indigo-500"
              required
            />
          </div>

          {/* Optional Notes */}
          <div>
            <label className="text-xs font-semibold text-textprimary mb-1 block">
              Notes / Remarks <span className="text-textsecondary text-[11px] font-normal">(Optional)</span>
            </label>
            <textarea
              rows={2}
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="e.g. Near main water pump panel, keys with security desk"
              className="w-full border border-gray-300 dark:border-bordercolor rounded-lg p-2.5 text-xs bg-white dark:bg-cardbg text-textprimary focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          {/* GPS Geofence Box */}
          <div className="p-3.5 border border-indigo-100 dark:border-bordercolor rounded-xl bg-indigo-50/40 dark:bg-dashboardbg/40 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-xs font-bold text-indigo-900 dark:text-indigo-300 flex items-center gap-1.5">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="text-red-500" />
                Anti-Spoofing GPS Geofence
              </span>
              <button
                type="button"
                onClick={handleGetCurrentLocation}
                className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
              >
                <FontAwesomeIcon icon={faCrosshairs} />
                Pin Current Coordinates
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-medium text-textsecondary block mb-1">Latitude</label>
                <input
                  type="number"
                  step="any"
                  placeholder="e.g. 19.076090"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                  className="w-full border border-gray-300 dark:border-bordercolor rounded-lg p-2 text-xs bg-white dark:bg-cardbg text-textprimary"
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-textsecondary block mb-1">Longitude</label>
                <input
                  type="number"
                  step="any"
                  placeholder="e.g. 72.877426"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                  className="w-full border border-gray-300 dark:border-bordercolor rounded-lg p-2 text-xs bg-white dark:bg-cardbg text-textprimary"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] font-medium text-textsecondary block mb-1">
                Scan Radius (Meters)
              </label>
              <input
                type="number"
                value={formData.geofenceRadiusMeters}
                onChange={(e) => setFormData({ ...formData, geofenceRadiusMeters: e.target.value })}
                className="w-full border border-gray-300 dark:border-bordercolor rounded-lg p-2 text-xs bg-white dark:bg-cardbg text-textprimary"
              />
              <span className="text-[10px] text-textsecondary mt-0.5 block">
                Field staff must be within this distance when scanning the QR tag.
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              id="activeToggle"
              checked={formData.isActiveId === 1}
              onChange={(e) => setFormData({ ...formData, isActiveId: e.target.checked ? 1 : 0 })}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
            />
            <label htmlFor="activeToggle" className="text-xs font-semibold text-textprimary cursor-pointer">
              Active for Field Patrols
            </label>
          </div>
        </form>
      </CommonModal>

      {/* QR Code Single Preview Modal */}
      <CommonModal
        isOpen={Boolean(previewQrItem)}
        onClose={() => setPreviewQrItem(null)}
        title="Patrol Checkpoint QR Tag"
        subTitle="Scan with Hello Commercial staff mobile app during patrol."
        footerContent={
          <div className="flex justify-center gap-3">
            <button
              onClick={() => {
                const locId = previewQrItem.locationId || previewQrItem.id;
                setSelectedForPrint([locId]);
                setIsPrintModalOpen(true);
                setPreviewQrItem(null);
              }}
              className="px-4 py-2 text-xs font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 flex items-center gap-1.5 shadow-sm"
            >
              <FontAwesomeIcon icon={faPrint} />
              Print Single Sticker
            </button>
          </div>
        }
      >
        {previewQrItem && (
          <div className="flex flex-col items-center text-center py-2 space-y-3">
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
              OFFICIAL PATROL POINT
            </span>
            <div className="text-sm font-bold text-textprimary">{previewQrItem.locationName}</div>
            <div className="px-3 py-1 rounded bg-slate-100 font-mono text-xs font-bold text-slate-800">
              {previewQrItem.locationCode}
            </div>

            <div className="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm inline-block">
              <QRCodeSVG value={previewQrItem.locationCode} size={160} level="H" includeMargin={true} />
            </div>

            <p className="text-xs text-textsecondary">
              Position this sticker securely at the physical inspection point.
            </p>
          </div>
        )}
      </CommonModal>

      {/* Batch Printable QR Sticker Sheet Modal */}
      <CommonModal
        isOpen={isPrintModalOpen}
        onClose={() => setIsPrintModalOpen(false)}
        title="Print Checkpoint QR Sticker Sheet"
        subTitle="Select checkpoints to include on print sheet. Ready for standard label paper."
        footerContent={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsPrintModalOpen(false)}
              className="px-4 py-2 text-xs font-medium rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-700"
            >
              Close
            </button>
            <button
              onClick={handleTriggerPrint}
              className="px-4 py-2 text-xs font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 flex items-center gap-1.5 shadow-sm"
            >
              <FontAwesomeIcon icon={faPrint} />
              Print Selected Stickers
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="bg-blue-50/60 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-xl p-3 flex justify-between items-center text-xs text-blue-900 dark:text-blue-300">
            <span>Grid view below represents how tags will print. Check/uncheck tags to filter.</span>
            <button
              onClick={handleTriggerPrint}
              className="font-bold underline text-indigo-600 dark:text-indigo-400"
            >
              Print Now
            </button>
          </div>

          {/* Printable Sticker Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 max-h-[55vh] overflow-y-auto p-1">
            {filteredLocations
              .filter((l) => selectedForPrint.includes(l.locationId || l.id))
              .map((loc) => {
                const locId = loc.locationId || loc.id;
                return (
                  <div
                    key={locId}
                    className="border border-gray-200 dark:border-bordercolor rounded-xl p-3.5 text-center bg-white dark:bg-cardbg shadow-sm relative flex flex-col items-center justify-between min-h-[220px]"
                  >
                    <div
                      className="absolute top-2 right-2 cursor-pointer z-10"
                      onClick={() => togglePrintSelect(locId)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedForPrint.includes(locId)}
                        onChange={() => {}}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 cursor-pointer"
                      />
                    </div>

                    <div className="text-[9px] font-bold uppercase tracking-wider text-textsecondary">
                      Patrol Checkpoint
                    </div>
                    <div className="text-xs font-bold text-textprimary truncate max-w-[180px]" title={loc.locationName}>
                      {loc.locationName}
                    </div>
                    <div className="px-2 py-0.5 rounded bg-slate-100 font-mono text-[10px] font-bold text-slate-800 my-1">
                      {loc.locationCode}
                    </div>

                    <div className="p-1.5 bg-white border border-gray-100 rounded-lg shadow-xs">
                      <QRCodeSVG value={loc.locationCode} size={95} level="H" includeMargin={true} />
                    </div>

                    <div className="text-[8px] text-textsecondary mt-1">
                      Hello Commercial &bull; Security & Patrol
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </CommonModal>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Deactivate Checkpoint"
        description={`Are you sure you want to deactivate checkpoint "${itemToDelete?.locationName}" (${itemToDelete?.locationCode})? Staff will no longer see this checkpoint in upcoming patrol rounds.`}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default CheckpointMasterList;
